import { ReactComponent as EditIcon } from "../../assets/icons/tables/edit-icon.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/tables/trash-icon.svg";
import { useState, useEffect } from "react";
import { DeleteModal } from "../../components";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useAppInfo } from "../../store/useAppInfo";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

dayjs.extend(relativeTime);

interface SortableItemProps {
  id: string;
  item: any;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

function SortableItem({ id, item, onEdit, onDelete }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? "#f3f4f6" : "white",
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="hover:bg-gray-100 cursor-pointer transition ease-linear border-b"
    >
      <td
        onClick={() => onEdit(item._id)}
        className="px-2 sm:px-6 py-1 sm:py-4 break-all sm:whitespace-nowrap"
      >
        {item?.title}
      </td>
      <td
        onClick={() => onEdit(item._id)}
        className="px-2 sm:px-6 py-1 sm:py-4"
      >
        {item?.creator?.first_name} {item?.creator?.last_name}
      </td>
      <td
        onClick={() => onEdit(item._id)}
        className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
      >
        {dayjs(item?.createdAt).fromNow()}
      </td>
      <td className="px-2 sm:px-6 py-1 sm:py-4 flex items-center justify-end cursor-pointer">
        <EditIcon
          onClick={() => onEdit(item._id)}
          className="mr-3 sm:mr-7"
        />
        <TrashIcon
          onClick={() => onDelete(item._id)}
          className="mr-3 sm:mr-7"
        />
        <div {...attributes} {...listeners} className="cursor-move">
          <img
            alt="drag-icon"
            src={require("../../assets/icons/tables/drag-icon.png")}
            className="w-[18px] h-[18px] mr-5"
          />
        </div>
      </td>
    </tr>
  );
}

export const AppInfoTable = () => {
  /** Hooks */
  const { appInfos, deleteAppInfo, updateAppInfosOrder } = useAppInfo();
  const navigate = useNavigate();
  /** State */
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState(appInfos);

  useEffect(() => {
    setItems(appInfos);
  }, [appInfos]);

  const toggleModal = () => setIsOpen(!isOpen);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item._id === active.id);
      const newIndex = items.findIndex((item) => item._id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        order: index + 1,
      }));

      setItems(updatedItems);
      let itemsToUpdate = updatedItems?.map((item) => ({ _id: item._id as string, order: item.order as number }));
      updateAppInfosOrder(itemsToUpdate);
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/edit-appinfo/${id}`);
  };

  const handleDelete = (id: string) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this App Info?"
    );
    if (confirmDelete) {
      deleteAppInfo(id);
    }
  };

  const itemIds = items.map((item) => item._id as UniqueIdentifier);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="relative overflow-x-auto">
        <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
          <thead className="font-medium bg-gray-50">
            <tr>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
                Title
              </th>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
                Author
              </th>
              <th
                scope="col"
                className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
              >
                Date
              </th>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              {items?.map((item) => (
                <SortableItem
                  key={item._id}
                  id={item._id as string}
                  item={item}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </SortableContext>
          </tbody>
        </table>
        <DeleteModal isOpen={isOpen} closeModal={toggleModal} />
      </div>
    </DndContext>
  );
};
